import type { Action, PayloadAction } from '@reduxjs/toolkit';

export const CUSTOM_PROPERTY_TYPE_PROPERTY = 1;
export const CUSTOM_PROPERTY_TYPE_CONTRACT = 2;
export const CUSTOM_PROPERTY_TYPE_WORK = 3;
/** 設定可能なカスタムプロパティの上限値 */
export const CUSTOM_PROPERTY_LIMIT = 20;
/** カスタムプロパティの選択肢数の上限値 */
export const CUSTOM_PROPERTY_OPTIONS_LIMIT = 20;

export type CustomPropertyState = {
  /** リフレッシュ */
  refresh: Record<string, never>;
  /** Load中か */
  isLoading: boolean;
  /** 送信処理中か */
  isSubmitting: boolean;
  /** カスタムプロパティのレイアウト */
  customPropertyLayoutList: CustomPropertyLayout[];
  /** カスタムプロパティの設定情報を保持する */
  customPropertyLayoutSettingList: CustomPropertyLayoutSetting[];
};

export enum CustomPropertyActionTypes {
  INIT = 'customProperty/init',
  REFRESH = 'customProperty/refresh',
  LOADING = 'customProperty/set/isLoading',
  SUBMITTING = 'customProperty/set/isSubmitting',
  SET_CUSTOM_PROPERTY_LAYOUT_LIST = 'customProperty/set/customPropertyLayoutList',
  SET_CUSTOM_PROPERTY_LAYOUT_SETTING_LIST = 'customProperty/set/customPropertyLayoutSetting',
}

export type CustomPropertyAction =
  | Action<CustomPropertyActionTypes.INIT>
  | Action<CustomPropertyActionTypes.REFRESH>
  | PayloadAction<boolean, CustomPropertyActionTypes.LOADING>
  | PayloadAction<boolean, CustomPropertyActionTypes.SUBMITTING>
  | PayloadAction<CustomPropertyLayout[], CustomPropertyActionTypes.SET_CUSTOM_PROPERTY_LAYOUT_LIST>
  | PayloadAction<
      CustomPropertyLayoutSetting[],
      CustomPropertyActionTypes.SET_CUSTOM_PROPERTY_LAYOUT_SETTING_LIST
    >;

/** カスタムプロパティを使用する機能IDリスト */
export const CustomPropertyUsedFunctionList = [
  CUSTOM_PROPERTY_TYPE_PROPERTY,
  CUSTOM_PROPERTY_TYPE_CONTRACT,
  CUSTOM_PROPERTY_TYPE_WORK,
] as const;

/** カスタムプロパティを使用する機能IDを示す型 */
export type CustomPropertyUsedFunction = (typeof CustomPropertyUsedFunctionList)[number];

/** カスタムプロパティの入力パターン */
export const customPropertyTypeList = [
  /** テキスト入力(一行) */
  'TextBox',
  /** テキスト入力(複数行) */
  'TextArea',
  /** 数値入力 */
  'NumberInput',
  /** 選択(コンボボックス) */
  'ComboBox',
  /** 選択(ラジオボタン) */
  'Choice',
  /** 日付 */
  'Date',
  /** チェックボックス */
  'Check',
] as const;

/** カスタムプロパティの入力パターンを示す型 */
export type CustomPropertyType = (typeof customPropertyTypeList)[number];

/**
 * @summary カスタムプロパティの型
 * @description 建物、契約、案件で設定可能なカスタムプロパティの型
 */
export type CustomProperty = {
  /** 項目名 */
  label: string;
  /** 入力パターン */
  type: CustomPropertyType;
  /** 値 */
  value: string[] | null;
  /** 表示フラグ */
  is_display: boolean;
};

/**
 * @summary カスタムプロパティの項目定義を示す型
 */
export type CustomPropertyLayout = {
  /** カスタムプロパティID */
  custom_property_id: number;
  /** 項目名 */
  label: string;
  /** 入力パターン */
  type: CustomPropertyType;
  /** 数値入力（下限値） */
  min_num: number | null;
  /** 数値入力（上限値） */
  max_num: number | null;
  /** 選択肢 */
  options: { value: string }[] | null;
  /** 並び順 */
  order: number;
  /** 必須入力か */
  required_flag: boolean;
  /** 表示フラグ */
  is_display: boolean;
};

/**
 * @summary カスタムプロパティの項目設定の入力フォームで使用する型
 */
export type CustomPropertyLayoutForm = {
  /** カスタムプロパティID */
  custom_property_id: number | null;
  /** 項目名 */
  label: string;
  /** 入力パターン */
  type: CustomPropertyType;
  /** 数値入力（下限値） */
  min_num: number | string | null;
  /** 数値入力（上限値） */
  max_num: number | string | null;
  /** 選択肢 */
  options: { value: string }[] | null;
  /** 並び順 */
  order: number | null;
  /** 必須入力か */
  required_flag: boolean;
  /** 表示フラグ */
  is_display: boolean;
};

/**
 * @summary カスタムプロパティによる詳細検索で使用する型
 */
export type CustomPropertySearchFromData = {
  custom_property_list: CustomPropertySearchFormValue[];
};

/**
 * @summary カスタムプロパティによる詳細検索で使用する型
 */
export type CustomPropertySearchFormValue = {
  /** カスタムプロパティID */
  custom_property_id: number;
  /** 項目名 */
  label?: string;
  /** テキスト入力(一行) */
  text_box: string;
  /** テキスト入力(複数行) */
  text_area: string;
  /** 選択肢 */
  options: { value: string }[];
  /** 数値入力（下限値） */
  min_num: number | string | null;
  /** 数値入力（上限値） */
  max_num: number | string | null;
  /** 日付（From） */
  since: string;
  /** 日付（To） */
  until: string;
};

/**
 * @summary 各機能毎のカスタムプロパティ項目の登録/編集で使用する型
 */
export type CustomPropertyEditForm = {
  custom_property_list: CustomPropertyEditFormValue[];
};

/**
 * @summary 各機能毎のカスタムプロパティ項目の登録/編集で使用する型
 */
export type CustomPropertyEditFormValue = {
  /** カスタムプロパティID */
  custom_property_id: number;
  /** 値 */
  value: string[];
};

/**
 * @summary カスタムプロパティの項目設定で使用する型
 */
export type CustomPropertyLayoutSetting = {
  /** 機能ID */
  type_id:
    | typeof CUSTOM_PROPERTY_TYPE_PROPERTY
    | typeof CUSTOM_PROPERTY_TYPE_CONTRACT
    | typeof CUSTOM_PROPERTY_TYPE_WORK;
  /** カスタムプロパティ項目定義リスト */
  custom_property_list: CustomPropertyLayout[];
};

/**
 * @summary カスタムプロパティの項目設定のリクエストで使用する型
 */
export type APIPostCustomPropertyLayoutSettingListRequest = {
  /** 機能ID */
  type_id:
    | typeof CUSTOM_PROPERTY_TYPE_PROPERTY
    | typeof CUSTOM_PROPERTY_TYPE_CONTRACT
    | typeof CUSTOM_PROPERTY_TYPE_WORK;
  /** カスタムプロパティ項目定義リスト */
  custom_property_list: CustomPropertyLayoutForm[];
}[];

/** @summary 機能毎のカスタムプロパティの項目定義リスト取得APIの返却値 */
export type APIGetCustomPropertyLayoutListResponse = {
  result: {
    custom_property_list: CustomPropertyLayout[];
  };
};

/** @summary カスタムプロパティの項目定義全件取得APIの返却値 */
export type APIGetCustomPropertyLayoutSettingListResponse = {
  result: CustomPropertyLayoutSetting[];
};
